import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  TableSkeleton,
} from "../../../components/ui";
import {
  addSocialChannel,
  deleteSocialChannel,
  getSocialChannels,
  updateSocialChannel,
  updateSocialChannelIcon,
} from "../../../services/socialChannels";
import { readImageFile } from "../../../utils/files";
import SectionCard from "../legal/SectionCard";
import "../ads/ad-form.css";

const EMPTY_CHANNEL = { name: "", link: "", iconBase64: "" };

const SocialChannels = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  // Channel being edited (null: a new channel is being added).
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CHANNEL);
  const [iconName, setIconName] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const refresh = () =>
    getSocialChannels()
      .then((data) => setChannels(data?.data || []))
      .catch((error) => console.error("Error:", error));
  useEffect(() => {
    refresh().then(() => setLoading(false));
  }, []);

  const reset = () => {
    setEditing(null);
    setForm(EMPTY_CHANNEL);
    setIconName("");
  };

  const setField = (field) => (e) => {
    const { value } = e.target;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.link.trim()) {
      toast.error(t("Please Fill Social Channel Completely"));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateSocialChannel(editing._id, form);
        // The icon is only sent when a new one was picked.
        if (form.iconBase64) {
          await updateSocialChannelIcon(editing._id, {
            public_id: editing.public_id,
            iconBase64: form.iconBase64,
          });
        }
        toast.success(t("Social Channel updated successfully"));
      } else {
        await addSocialChannel(form);
        toast.success(t("Social Channel added successfully"));
      }
      reset();
      refresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error(t("Saving Social Channel Failed!"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (channel) => {
    try {
      await deleteSocialChannel(channel._id);
      toast.success(t("Social Channel deleted successfully!"));
      if (editing?._id === channel._id) reset();
      refresh();
    } catch (error) {
      console.error("Error:", error);
      toast.error(t("Saving Social Channel Failed!"));
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Social Channels")}
        subtitle={t("Links shown in the apps")}
      />

      <SectionCard
        title={editing ? t("Edit Social Channel") : t("Add Social Channel")}
        editing
        saving={saving}
        onCancel={editing || form.name || form.link ? reset : undefined}
        onSave={handleSave}
      >
        <div className="ui-form">
          <div className="ui-field">
            <label htmlFor="social-name">{t("Social Channel Name")}</label>
            <input
              id="social-name"
              className="ui-input"
              placeholder="Facebook"
              value={form.name}
              onChange={setField("name")}
            />
          </div>
          <div className="ui-field">
            <label htmlFor="social-link">{t("Social Channel Link")}</label>
            <input
              id="social-link"
              className="ui-input"
              type="url"
              placeholder="https://www.facebook.com/…"
              value={form.link}
              onChange={setField("link")}
            />
          </div>
          <div className="ui-field ui-field-full">
            <span className="ui-label">{t("Icon")}</span>
            <div className="content-banner-actions">
              {form.iconBase64 && (
                <img className="social-icon" src={form.iconBase64} alt="" />
              )}
              <label htmlFor="social-icon" className="ad-upload">
                <i className="ri-upload-2-line" aria-hidden="true"></i>
                {iconName || t("Upload Icon")}
              </label>
              <input
                id="social-icon"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  setIconName(file.name);
                  readImageFile(file, (iconBase64) =>
                    setForm((current) => ({ ...current, iconBase64 }))
                  );
                  e.target.value = "";
                }}
              />
            </div>
            {editing && (
              <span className="ui-hint">{t("Leave empty to keep the current icon")}</span>
            )}
          </div>
        </div>
      </SectionCard>

      <section className="ui-card">
        {loading ? (
          <TableSkeleton />
        ) : channels.length === 0 ? (
          <EmptyState icon="ri-share-line">{t("No Data Found")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>{t("Social Channel Name")}</th>
                  <th>{t("Social Channel Link")}</th>
                  <th className="ui-cell-actions">
                    <span className="sr-only">{t("action")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((channel) => (
                  <tr
                    key={channel._id}
                    className={editing?._id === channel._id ? "is-selected" : undefined}
                  >
                    <td>
                      <span className="ui-media-title">{channel.name}</span>
                    </td>
                    <td>
                      <a
                        className="ui-clamp social-link"
                        style={{ maxWidth: 420 }}
                        href={channel.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {channel.link}
                      </a>
                    </td>
                    <td className="ui-cell-actions">
                      <IconButton
                        icon="ri-pencil-line"
                        label={t("Edit")}
                        onClick={() => {
                          setEditing(channel);
                          setForm({ name: channel.name || "", link: channel.link || "", iconBase64: "" });
                          setIconName("");
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      />
                      <IconButton
                        icon="ri-delete-bin-line"
                        label={t("Delete")}
                        tone="danger"
                        onClick={() => setToDelete(channel)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {toDelete && (
        <Popup
          open={true}
          setOpen={() => setToDelete(null)}
          deleteHandle={() => handleDelete(toDelete)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")} {toDelete.name}?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default SocialChannels;
